export type {
  Primitive,
  JsonValue,
  HttpMethod,
  Authentication,
  Parameter,
  Connector,
  ExecuteConnectorOptions,
  ExecutePayload,
  ExecuteResponse,
  SDKConfig,
  DeveloperUserContext,
  DeveloperModeOptions
} from "./types";

import type {
  ExecuteConnectorOptions,
  ExecuteResponse,
  SDKConfig,
  DeveloperModeOptions
} from "./types";

const HTTP_METHODS_WITH_BODY = ["POST", "PUT", "PATCH"];
const DEFAULT_INSTANCE_URL =
  "https://widget-service.northpass.com/api/public/v1";

class WidgetServiceSDK {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private csrfToken?: string;
  private isBrowser: boolean;
  private defaultTimeout: number;
  private developerMode: boolean = false;
  private developerApiKey: string = "";
  private developerUserContext: string = "";

  constructor(config: SDKConfig = {}) {
    this.isBrowser =
      typeof window !== "undefined" && typeof document !== "undefined";

    // Always use widget-service path from current origin in browser
    if (this.isBrowser) {
      this.baseUrl = `${window.location.origin}/widget-service`;
    } else {
      throw new Error("SDK can only be used in browser environments");
    }

    // Handle CSRF token
    if (config.csrfToken) {
      this.csrfToken = config.csrfToken;
    }

    this.defaultHeaders = {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...config.headers
    };

    this.defaultTimeout = config.timeout || 30000;
  }

  private waitForDOMReady(): Promise<void> {
    if (!this.isBrowser) {
      return Promise.resolve();
    }

    if (document.readyState === "loading") {
      return new Promise((resolve) => {
        document.addEventListener("DOMContentLoaded", () => resolve(), {
          once: true
        });
      });
    }

    return Promise.resolve();
  }

  private getCsrfToken(): string {
    const el = document.getElementById("csrftoken");
    if (!el) return "";

    // Try different ways to get the token value
    const inputEl = el as HTMLInputElement;
    return (
      inputEl.value ||
      el.getAttribute("content") ||
      el.textContent ||
      el.innerText ||
      ""
    );
  }

  private async makeRequest<T>(
    url: string,
    options: RequestInit = {},
    additionalHeaders?: Record<string, string>
  ): Promise<T> {
    // Get CSRF token dynamically for each request
    const csrfToken =
      this.csrfToken || (this.isBrowser ? this.getCsrfToken() : "");

    // Prepare headers
    const headers: Record<string, string> = {
      ...this.defaultHeaders
    };

    // Add CSRF token if available
    if (csrfToken) {
      headers["x-csrf-token"] = csrfToken;
    }

    // Add request-specific headers
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    // Add additional headers
    if (additionalHeaders) {
      Object.assign(headers, additionalHeaders);
    }

    // Add developer mode headers if enabled
    if (this.developerMode) {
      headers["X-Developer-Mode-Api-Key"] = this.developerApiKey;
      headers["X-Developer-Mode-User-Context"] = this.developerUserContext;
    }

    // Set up abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.defaultTimeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage: string;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage =
            errorJson.error ||
            errorJson.message ||
            `HTTP ${response.status}: ${response.statusText}`;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new Error(`Request timeout after ${this.defaultTimeout}ms`);
        }
        throw error;
      }

      throw new Error("Unknown error occurred");
    }
  }

  public connectors = {
    /**
     * Execute a connector with the given payload and HTTP method
     * @param options - Options for executing the connector including the HTTP method
     * @returns Promise resolving to the execution response
     */
    execute: async (
      options: ExecuteConnectorOptions
    ): Promise<ExecuteResponse> => {
      await this.waitForDOMReady();

      const baseURL = new URL(this.baseUrl);
      const pathname = baseURL.pathname.replace(/\/$/, "");
      const url = new URL(
        `${pathname}/connectors/${options.permalink}/execute`,
        baseURL.origin
      );

      baseURL.searchParams.forEach((value, key) => {
        url.searchParams.set(key, value);
      });

      if (options.queryParams) {
        Object.entries(options.queryParams).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
      }

      const requestOptions: RequestInit = {
        method: options.method
      };

      if (HTTP_METHODS_WITH_BODY.includes(options.method) && options.payload) {
        if (typeof options.payload !== "object") {
          throw new Error("Payload must be a valid JSON object");
        }
        requestOptions.body = JSON.stringify(options.payload);
      }

      return this.makeRequest<ExecuteResponse>(
        url.toString(),
        requestOptions,
        options.headers
      );
    }
  };

  /**
   * Enable developer mode for testing against a specific instance API
   * @param options - Developer mode configuration options
   */
  public enableDeveloperMode(options: DeveloperModeOptions): void {
    if (!options.apiKey) {
      throw new Error("API key is required for developer mode");
    }

    this.developerMode = true;
    this.developerApiKey = options.apiKey;

    // Override base URL for developer mode if instanceUrl is provided
    if (options.instanceUrl) {
      this.baseUrl = options.instanceUrl.replace(/\/$/, "");
    } else {
      this.baseUrl = DEFAULT_INSTANCE_URL;
    }

    // Base64 encode the user context
    this.developerUserContext = btoa(JSON.stringify(options.userContext));
  }

  /**
   * Disable developer mode and return to normal operation
   */
  public disableDeveloperMode(): void {
    this.developerMode = false;
    this.developerApiKey = "";
    this.developerUserContext = "";

    // Restore original baseUrl when exiting developer mode
    if (this.isBrowser) {
      this.baseUrl = `${window.location.origin}/widget-service`;
    }
  }

  /**
   * Check if SDK is currently in developer mode
   * @returns boolean indicating if developer mode is active
   */
  public isInDeveloperMode(): boolean {
    return this.developerMode;
  }

  /**
   * Set API key for authentication
   * @param apiKey - The API key to use for authentication
   */
  public setApiKey(apiKey: string): void {
    this.defaultHeaders["Authorization"] = `Bearer ${apiKey}`;
  }

  /**
   * Remove API key from headers
   */
  public clearApiKey(): void {
    delete this.defaultHeaders["Authorization"];
  }

  /**
   * Set default timeout for all requests
   * @param timeoutMs - Timeout in milliseconds
   */
  public setDefaultTimeout(timeoutMs: number): void {
    this.defaultTimeout = timeoutMs;
  }

  /**
   * Update default headers
   * @param headers - Headers to merge with existing defaults
   */
  public updateHeaders(headers: Record<string, string>): void {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  /**
   * Get the current base URL
   */
  public getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Get current default headers
   */
  public getHeaders(): Record<string, string> {
    return { ...this.defaultHeaders };
  }

  /**
   * Get current default timeout
   */
  public getDefaultTimeout(): number {
    return this.defaultTimeout;
  }

  /**
   * Get the current CSRF token from the page
   * @returns CSRF token string or empty string if not found
   */
  public getCsrfTokenValue(): string {
    return this.isBrowser ? this.getCsrfToken() : "";
  }

  /**
   * Check if CSRF token is available on the page
   * @returns boolean indicating if CSRF token element exists
   */
  public hasCsrfToken(): boolean {
    return this.isBrowser
      ? document.getElementById("csrftoken") !== null
      : false;
  }
}

// Create default instance
const defaultSDK = new WidgetServiceSDK();

// Export both the class and default instance
export { WidgetServiceSDK };
export default defaultSDK;

// Global window attachment for UMD usage
declare global {
  interface Window {
    WidgetServiceSDK: typeof WidgetServiceSDK;
  }
}

// Attach to window if in browser environment
if (typeof window !== "undefined") {
  window.WidgetServiceSDK = WidgetServiceSDK;
}
